// client/src/scenes/GameplayScene.js

import Phaser from 'phaser';
import GestureRecognizer from '../utils/gestureRecognition';
import CameraManager from '../utils/cameraAccess';

class GameplayScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameplayScene' });

        // Add these new properties
        this.gameTime = 60; // 60 second game by default
        this.timeRemaining = 60;
        this.timerText = null;
        this.timerBar = null;
        this.level = 1;
        this.levelText = null;
        this.isGameOver = false;
        this.targetMoveSpeed = 1;
        this.targetTypes = ['generic']; // Will expand with different target types as levels progress

        // Game state
        this.score = 0;
        this.combo = 0;
        this.maxCombo = 0;

        // Gesture tracking
        this.gestureRecognizer = null;
        this.cameraManager = null;
        this.currentGesture = null;
        this.currentVelocity = 0;
        this.currentDirection = { x: 0, y: 0 };

        // Game objects
        this.targetSprite = null;
        this.scoreText = null;
        this.comboText = null;
        this.feedbackText = null;
        this.swingGuideGraphics = null;

        // Camera feed
        this.videoElement = null;
        this.videoTexture = null;
        this.videoSprite = null;

        // Feedback messages
        this.feedbackMessages = {
            flat: [
                "Flat as their arguments!",
                "Smoother than their talk show appearances!",
                "That's one flat smack!"
            ],
            fist: [
                "Fist bump of justice!",
                "Pound that podium!",
                "Knuckle sandwich served!"
            ],
            guns: [
                "Finger guns of truth!",
                "Pointing out the obvious!",
                "Right on target!"
            ]
        };
    }

// Add this to the preload method
    preload() {
        // Create placeholder target character
        this.load.spritesheet('target-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMAAAAAQCAYAAABA4nAoAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAJVSURBVHhe7ZiLkYMgEIZpIAWkgTSQBtJAGkgDaSANpIE0sA3cXgQWUFHzMJn8M9+Mx8PjArvLGhNFURRFURRFURRFURRF0Rbc9/Y0YNzsM2jZbIQgeIDmDkN3N7fpbuvRvU+v2Zxxs03oMm4bsMsQ9fvdDLZ1czpXMPTpZjrXmDjHYjwNjH/K8g6YFEi5/ZoImKRIVrFfEBVAlAAR5lVA0VEKiAqgUAFECRBhogIoVABRAkSYqAAKFUCUABHmmwK0ZjSVqcxgWnR1oMXjgcdx2NlMptFnYNt3vRnRXqNlnDLtRiPD15ftzjv7cV/HsQI+iK6N4HsuUq7xClAZLyGDDxEeEBLCJ4o9XgGuNHLPj1cHt45c+eQs99nIlnBLBcgl2eNI0oEjRbqYDZ9kKpkWpQfpvfzwHsvuPQF0ULntGcRWQ4jASXfNE2CefXyNBvfZSXLdqgvU53VzRGtH57hQtNZ2JkXz4jF83pL2zcRXNlSiT/NKz7+lAlB0u/rMb2Jf7kluWlJfj/j4jLBLjncSLhpk4Cz1JJNvRbQ1O9CRoT69kS7XD5HoCsjJHtBc/+yZlKNn2fKIIiTq9n8y8XOUDxJHfQCjzgdfZxgMX3sPnCvAeJ4Aib9Y+YbLZCxVPvt8sgK4KLBs4HrpnC/eABLQBbK99cGw1eArxDERjwsAt0lEQ5/hAojOQdHPFZ+Py1s4m/sKUlEcxvEewJGvWdzJCoBM4rp15RVXu5YJZrwO0kGiKIqiKIqiKIqiKIqiKIqiKIr+A2b+ALVwnRHKVQXPAAAAAElFTkSuQmCC', {
            frameWidth: 128,
            frameHeight: 32
        });

        // Create placeholder effect sprites
        this.load.spritesheet('effects-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAABAAQMAAABL1mI7AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURf///wAAAFXC034AAAAJcEhZcwAADsMAAA7DAcdvqGQAAAEhSURBVEjH1ZSxbcMwEEWZIYM4GxgZIBtoBGUEj5ANMoJGyAgawSN4A2eD69/xKFmWZbSoiuCBIHn3/3EESel/rRc70PUKxLvYgeyhg6y4ZOwt+m7CUjlnnHvlnC0wJODeDVCkDgd8alsHxEQpLRKwBQ6QKGVQToRSEgdMzYAS8q7GWErpARMlOmfr5EzJmbC9Y4DUyBmvnG+e8H4qNNPeA9qsJZQS/LxGKH1fYBLlhLI2waOUwdH2Y5Q8wPbyGiUPWGCUFKUtwCh5wK3YsVxQamS/hP1CyQNCyL5pnzc6QEMpA0pMqYkBXfuVqANSV+DbIjpAcyBigBelJAYE1+5TAWVEKYEBXpQcGODbdiw5KgBSPgMgZWJAcGBkgBclo3Q8fg9w/Nv6AlmWH6GJikG1AAAAAElFTkSuQmCC', {
            frameWidth: 64,
            frameHeight: 64
        });

        // Create placeholder gesture icons
        this.load.image('flat-hand-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAADJSURBVFhH7ZZBCsIwEEXnKN7AjbfwJl7AhTfxHq48RFcewK28gRvdCB0ZCKFtJpmmUOiDDwXTzJ8/tKHNZDL/zD7UsQnbmocw0/Qdw1zLDdTkJV/DG/iAE3iGLTiBM+i1aFLKPVzCCg7hHjLWYnpuRPSH7hgSzJ3DMdRiD9DgdNdQi42QTkvKg56JLkYFZK+ANe/hDjKnecFkA3VaUh70XOyv6GJUgK+W+qfgJXfwCpmbJnWCyQbqtKQ86JnoYlQAkyygCXfQZDKZb8rSBwy9BKhaskDTAAAAAElFTkSuQmCC');
        this.load.image('fist-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAADNSURBVFhH7ZZBDoIwEEWnxr2ewBN4Ag/gzp0n8CIewYVn8CxewI3xBE5MJIYwhQ7TTiH8pAtCO/PnD21pM5nMP7MPdWzClrOHz/ACX/ABb/AM9/AEO2gTLeUaLmAF13ALGWsxPX9I9KXuOCWYO4Rj6MVWQIPS3UMvNkM+LSkPeiq6mC0hO0K2gDUfIRPLXmYnmJ2gTkvKg56K/RVdzJaQvYrQPwXvcAWZu09qgtkJ6rSkPOip6GK2hMymJeVBT0UXsyVkNi0pD3oqupjJ/BZT+gJXjATrNnKjpQAAAABJRU5ErkJggg==');
        this.load.image('finger-guns-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAEPSURBVFhH7ZZLCsIwEIbnKG58nMCVJ/AGLryJexdu3OtWD+DjBvUGuvcAKrjVTGSgxCZpJqlQ6A8fhWb+mT9taTOZTBYGYeCGDXCZnzfwBb/wDT/wDV+QRbRQxPEJXuEeLiDLRUF5UEti0MUo8A1VWkbwCLkRbpwVZhOqtKQyaCnRD+ljVECvlZnXyppfoIiVdxpvWPo6OtHYZNAyontEe0QXowK67hLXIwdnudvv1/YAzToLCeYOcOcPnMErZK45UbFZ6rSkMmgp0Q/pY1RApZWJ3oJVW0/wDbLCbEKVllQGLSW6R7RHdDEqoG7r1xzFWlke1n3CFRRx9NpQLHWdYOkrFZtldILoHtE9ootRAbUmk0n8R6YPbkwBVecdhMQAAAAASUVORK5CYII=');

        // Placeholder for game background
        this.load.image('game-bg-placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABAAAAGAAAAAgAQAAAACMK6h1AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURf///wAAAFXC034AAAAJcEhZcwAADsMAAA7DAcdvqGQAAABVSURBVHja7dYxDcAwDATAZ2ZmZupWKBJGIAVkfsKIA8nSVa+vVx8BAADkH0BXpTYzCAAAAAAAAAAAAJifAQAAAAD+BrhrOQQAAAAAAAAAAADAD4zHK1GMDH5QAAAAAElFTkSuQmCC');

        // Placeholder sound effects
        this.load.audio('slap-sound', 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA=');
        this.load.audio('punch-sound', 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA=');
        this.load.audio('shoot-sound', 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA=');
        this.load.audio('combo-sound', 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA=');
    }

// Make sure the create method initializes properly
    create() {
        console.log('GameplayScene create started');

        try {
            // Create a simple background
            this.add.rectangle(400, 300, 800, 600, 0x000000);

            // Create score display first so it's always available
            this.scoreText = this.add.text(20, 20, 'Score: 0', {
                fontFamily: 'Arial',
                fontSize: 24,
                color: '#ffffff'
            });

            // Create other UI elements
            this.createUI();

            // Create target character
            this.targetSprite = this.add.circle(600, 300, 40, 0xff0000);
            this.targetSprite.setInteractive();

            // Create swing guide overlay
            this.swingGuideGraphics = this.add.graphics();
            this.updateSwingGuide();

            // Create video feed display
            this.createVideoFeed();

            // Initialize camera access
            this.initializeCamera();

            console.log('GameplayScene create completed successfully');
        } catch (error) {
            console.error('Error in GameplayScene create:', error);

            // Show error message
            this.add.text(400, 300, 'Error initializing game. Please try again.', {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff',
                align: 'center',
                backgroundColor: '#aa0000',
                padding: { x: 20, y: 10 }
            }).setOrigin(0.5);

            // Add a button to go back to main menu
            const backButton = this.add.rectangle(400, 350, 200, 40, 0x4CAF50);
            backButton.setInteractive();

            this.add.text(400, 350, 'Back to Menu', {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);

            backButton.on('pointerup', () => {
                this.scene.start('MainMenuScene');
            });
        }
    }



    // Add these new methods for timer and level management
    startGameTimer() {
        // Reset time
        this.timeRemaining = this.gameTime;
        this.isGameOver = false;

        // Update the timer display
        this.updateTimerDisplay();

        // Create a timer event
        this.timerEvent = this.time.addEvent({
            delay: 1000,
            callback: this.updateTimer,
            callbackScope: this,
            loop: true
        });
    }

    updateTimer() {
        if (this.isGameOver) return;

        // Decrease time
        this.timeRemaining--;

        // Update the timer display
        this.updateTimerDisplay();

        // Check if time is up
        if (this.timeRemaining <= 0) {
            this.endGame();
        }

        // Flash timer when low on time
        if (this.timeRemaining <= 10) {
            // Flash the timer when low on time
            this.timerText.setTint(0xff0000);
            this.tweens.add({
                targets: this.timerText,
                scale: 1.2,
                duration: 300,
                yoyo: true,
                ease: 'Power1'
            });
        }
    }

    updateTimerDisplay() {
        // Update text
        this.timerText.setText(`${this.timeRemaining}`);

        // Update timer bar
        const timerRatio = this.timeRemaining / this.gameTime;
        this.timerBar.width = 200 * timerRatio;

        // Change color based on time remaining
        if (this.timeRemaining <= 10) {
            this.timerBar.fillColor = 0xff0000; // Red when low on time
        } else if (this.timeRemaining <= 30) {
            this.timerBar.fillColor = 0xffff00; // Yellow for medium time
        } else {
            this.timerBar.fillColor = 0x00ff00; // Green for plenty of time
        }
    }

    endGame() {
        // Stop the timer
        if (this.timerEvent) {
            this.timerEvent.remove();
        }

        // Set game over flag
        this.isGameOver = true;

        // Stop gesture tracking
        if (this.gestureRecognizer) {
            this.gestureRecognizer.stopTracking();
        }

        // Create a game over effect
        const gameOverRect = this.add.rectangle(400, 300, 800, 600, 0x000000, 0);

        // Fade in the overlay
        this.tweens.add({
            targets: gameOverRect,
            alpha: 0.7,
            duration: 1000,
            onComplete: () => {
                // Show "Time's Up!" text
                const timeUpText = this.add.text(400, 250, "Time's Up!", {
                    fontFamily: 'Arial',
                    fontSize: 48,
                    color: '#ffffff',
                    stroke: '#000000',
                    strokeThickness: 6
                }).setOrigin(0.5);

                // Add a final score display
                const finalScoreText = this.add.text(400, 320, `Final Score: ${this.score}`, {
                    fontFamily: 'Arial',
                    fontSize: 36,
                    color: '#ffff00',
                    stroke: '#000000',
                    strokeThickness: 4
                }).setOrigin(0.5);

                // Add max combo display
                const maxComboText = this.add.text(400, 370, `Max Combo: ${this.maxCombo}x`, {
                    fontFamily: 'Arial',
                    fontSize: 24,
                    color: '#ff9900',
                    stroke: '#000000',
                    strokeThickness: 3
                }).setOrigin(0.5);

                // Add continue button
                const continueButton = this.add.rectangle(400, 450, 200, 50, 0x4CAF50);
                continueButton.setInteractive({ useHandCursor: true });

                const continueText = this.add.text(400, 450, 'Continue', {
                    fontFamily: 'Arial',
                    fontSize: 20,
                    color: '#ffffff'
                }).setOrigin(0.5);

                // Add button events
                continueButton.on('pointerover', () => {
                    continueButton.fillColor = 0x66BB6A;
                });

                continueButton.on('pointerout', () => {
                    continueButton.fillColor = 0x4CAF50;
                });

                continueButton.on('pointerdown', () => {
                    continueButton.fillColor = 0x388E3C;
                });

                continueButton.on('pointerup', () => {
                    // Clean up and go to game over scene
                    this.cleanupResources();
                    this.scene.start('GameOverScene', {
                        score: this.score,
                        maxCombo: this.maxCombo,
                        level: this.level
                    });
                });
            }
        });
    }

    // Simplified placeholder for camera feed
    createVideoFeed() {
        try {
            // Create a placeholder for the video feed
            this.videoSprite = this.add.rectangle(200, 300, 320, 240, 0x222222);

            // Create a placeholder for the gesture indicator
            this.gestureIndicator = this.add.circle(200, 450, 30, 0xaaaaaa);
            this.gestureIndicator.setVisible(false); // Initially hidden

            // Add text to indicate this is where gestures will be shown
            this.gestureText = this.add.text(200, 500, 'Waiting for gesture...', {
                fontFamily: 'Arial',
                fontSize: 16,
                color: '#ffffff'
            }).setOrigin(0.5);

            // Add camera feed label
            this.add.text(200, 300, 'Camera Feed', {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);
        } catch (error) {
            console.error('Error in createVideoFeed:', error);
        }
    }

    createUI() {
        try {
            // Score display
            this.scoreText = this.add.text(20, 20, 'Score: 0', {
                fontFamily: 'Arial',
                fontSize: 24,
                color: '#ffffff'
            });

            // Combo display
            this.comboText = this.add.text(20, 60, 'Combo: 0x', {
                fontFamily: 'Arial',
                fontSize: 20,
                color: '#ffff00'
            });
            this.comboText.setVisible(false);

            // Feedback messages
            this.feedbackText = this.add.text(400, 150, '', {
                fontFamily: 'Arial',
                fontSize: 28,
                color: '#ffffff'
            }).setOrigin(0.5);
            this.feedbackText.setVisible(false);

            // Current gesture display
            this.gestureText = this.add.text(200, 550, 'Waiting for gesture...', {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);

            // Add a back button
            const backButton = this.add.rectangle(100, 50, 160, 40, 0xF44336);
            backButton.setInteractive();

            this.add.text(100, 50, 'Back to Menu', {
                fontFamily: 'Arial',
                fontSize: 16,
                color: '#ffffff'
            }).setOrigin(0.5);

            backButton.on('pointerup', () => {
                // Clean up and go back to menu
                this.cleanup();
                this.scene.start('MainMenuScene');
            });
        } catch (error) {
            console.error('Error in createUI:', error);
        }
    }

    createAnimations() {
        // Slap effect animation
        this.anims.create({
            key: 'slap-effect',
            frames: this.anims.generateFrameNumbers('effects', { start: 0, end: 4 }),
            frameRate: 12,
            repeat: 0
        });

        // Punch effect animation
        this.anims.create({
            key: 'punch-effect',
            frames: this.anims.generateFrameNumbers('effects', { start: 5, end: 9 }),
            frameRate: 12,
            repeat: 0
        });

        // Shoot effect animation
        this.anims.create({
            key: 'shoot-effect',
            frames: this.anims.generateFrameNumbers('effects', { start: 10, end: 14 }),
            frameRate: 12,
            repeat: 0
        });

        // Target character reactions
        this.anims.create({
            key: 'target-reaction',
            frames: this.anims.generateFrameNumbers('target', { start: 0, end: 3 }),
            frameRate: 8,
            repeat: 0
        });
    }

    // Simplified camera initialization
    async initializeCamera() {
        try {
            // Create a video element for the camera
            this.videoElement = document.createElement('video');
            this.videoElement.setAttribute('autoplay', true);
            this.videoElement.setAttribute('playsinline', true);

            // Add it to the DOM but hide it
            this.videoElement.style.display = 'none';
            document.body.appendChild(this.videoElement);

            // Create and initialize the camera manager
            this.cameraManager = new CameraManager();

            if (this.cameraManager.setCallbacks) {
                this.cameraManager.setCallbacks(
                    () => this.onCameraReady(),
                    (error) => this.onCameraError(error)
                );
            }

            // Try to initialize the camera
            const success = await this.cameraManager.initialize(this.videoElement);

            if (!success) {
                this.onCameraError('Failed to initialize camera');
            }
        } catch (error) {
            console.error('Error initializing camera:', error);
            this.onCameraError(error.message || 'Camera initialization failed');
        }
    }

    // Handle camera ready event
    onCameraReady() {
        console.log('Camera initialized successfully');

        // Create a simple camera view representation
        if (this.videoSprite) {
            this.videoSprite.fillColor = 0x555555; // Darken to show it's active

            // Add a camera icon
            this.add.text(200, 240, "📷", {
                fontSize: 32
            }).setOrigin(0.5);

            this.add.text(200, 300, "Camera Active", {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);
        }

        // Initialize the gesture recognizer
        this.initializeGestureRecognition();
    }

    // Handle camera error
    onCameraError(error) {
        console.error('Camera error:', error);

        // Show error message on screen
        this.add.text(400, 300, 'Camera access denied\nPlease enable camera access and reload', {
            fontFamily: 'Arial',
            fontSize: 24,
            color: '#ffffff',
            align: 'center'
        }).setOrigin(0.5);

        if (this.videoSprite) {
            this.videoSprite.fillColor = 0xff0000; // Red to show error

            this.add.text(200, 300, "Camera Error", {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff'
            }).setOrigin(0.5);
        }
    }

    async initializeGestureRecognition() {
        try {
            // Create the gesture recognizer
            this.gestureRecognizer = new GestureRecognizer();

            // Initialize with the video element
            const success = await this.gestureRecognizer.initialize(this.videoElement);

            if (success) {
                console.log('Gesture recognizer initialized successfully');

                // Register callbacks with error handling
                if (this.gestureRecognizer.registerCallbacks) {
                    this.gestureRecognizer.registerCallbacks(
                        (gesture) => this.onGestureDetected(gesture),
                        (velocity) => {
                            this.currentVelocity = velocity;
                            this.updateSwingGuide();
                        },
                        (direction) => {
                            this.currentDirection = direction;
                        }
                    );
                }

                // Start tracking gestures
                if (this.gestureRecognizer.startTracking) {
                    this.gestureRecognizer.startTracking();
                }
            } else {
                console.error('Failed to initialize gesture recognizer');
            }
        } catch (error) {
            console.error('Error in initializeGestureRecognition:', error);
        }
    }

    init(data) {
        // Get sound manager reference if passed
        if (data && data.soundManager) {
            this.soundManager = data.soundManager;
        }

        console.log('GameplayScene initialized');
    }

    // Update the gesture recognizer's trackGesturesDebug method to handle errors better
    async trackGesturesDebug() {
        console.log('Starting simulated gesture tracking');

        // Immediately trigger an "unknown" gesture to initialize the UI
        if (this.onGestureDetectedCallback) {
            try {
                this.onGestureDetectedCallback('unknown');
            } catch (error) {
                console.error('Error in initial gesture callback:', error);
            }
        }

        let lastGesture = 'unknown';

        while (this.isRunning) {
            await new Promise(resolve => setTimeout(resolve, 2000));

            if (!this.isRunning) break;

            try {
                // Randomly choose a gesture for demonstration
                const gestures = ['flat', 'fist', 'guns', 'unknown'];
                let randomGesture;

                // Slightly higher probability of keeping the same gesture
                // to make it feel more natural
                if (Math.random() < 0.3) {
                    randomGesture = lastGesture;
                } else {
                    randomGesture = gestures[Math.floor(Math.random() * gestures.length)];
                }

                lastGesture = randomGesture;

                // Generate random velocity (10-100)
                const velocity = 10 + Math.random() * 90;

                // Generate random direction
                const angle = Math.random() * Math.PI * 2;
                const direction = {
                    x: Math.cos(angle),
                    y: Math.sin(angle)
                };

                // Call the callbacks with a slight delay between them to simulate processing
                if (this.onGestureDetectedCallback) {
                    try {
                        this.onGestureDetectedCallback(randomGesture);
                    } catch (error) {
                        console.error('Error in gesture detection callback:', error);
                    }
                }

                await new Promise(resolve => setTimeout(resolve, 100));

                if (this.onVelocityCalculatedCallback) {
                    try {
                        this.onVelocityCalculatedCallback(velocity);
                    } catch (error) {
                        console.error('Error in velocity calculation callback:', error);
                    }
                }

                await new Promise(resolve => setTimeout(resolve, 100));

                if (this.onDirectionCalculatedCallback) {
                    try {
                        this.onDirectionCalculatedCallback(direction);
                    } catch (error) {
                        console.error('Error in direction calculation callback:', error);
                    }
                }
            } catch (error) {
                console.error('Error in gesture simulation:', error);
                // Wait a bit before trying again
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    onGestureDetected(gesture) {
        // Check if we have a valid gesture and indicator
        if (!gesture || !this.gestureIndicator) {
            return;
        }

        console.log('Gesture detected:', gesture);

        // Update the current gesture
        this.currentGesture = gesture;

        // Update the gesture indicator
        this.updateGestureIndicator(gesture);

        // Process the gesture if it changed
        if (this.previousGesture !== gesture) {
            this.previousGesture = gesture;
            this.processGesture(gesture);
        }
    }

    onVelocityCalculated(velocity) {
        this.currentVelocity = velocity;

        // Update swing guide based on velocity
        this.updateSwingGuide();
    }

    onDirectionCalculated(direction) {
        this.currentDirection = direction;
    }

    updateGestureIndicator(gesture) {
        try {
            if (!this.gestureIndicator || !this.gestureText) {
                console.log('Gesture indicator or text not initialized');
                return;
            }

            // Show the appropriate gesture icon
            this.gestureIndicator.setVisible(true);

            switch(gesture) {
                case 'flat':
                    this.gestureIndicator.fillColor = 0x00ff00; // Green
                    this.gestureText.setText('Gesture: Flat Hand');
                    break;
                case 'fist':
                    this.gestureIndicator.fillColor = 0xff0000; // Red
                    this.gestureText.setText('Gesture: Closed Fist');
                    break;
                case 'guns':
                    this.gestureIndicator.fillColor = 0xffff00; // Yellow
                    this.gestureText.setText('Gesture: Finger Guns');
                    break;
                default:
                    this.gestureIndicator.fillColor = 0x888888; // Gray
                    this.gestureText.setText('Gesture: Unknown');
            }
        } catch (error) {
            console.error('Error in updateGestureIndicator:', error);
        }
    }

    processGesture(gesture) {
        // Add guard clauses for undefined values
        if (!gesture || !['flat', 'fist', 'guns'].includes(gesture)) {
            return; // Skip processing for invalid gestures
        }

        // Make sure we have a valid target
        if (!this.targetSprite || !this.targetSprite.active) {
            return; // Skip if no valid target
        }

        // Calculate points based on velocity and combo
        const basePoints = 10;
        const velocityMultiplier = Math.min((this.currentVelocity || 0) / 20, 5);
        const points = Math.floor(basePoints * velocityMultiplier * (1 + (this.combo || 0) * 0.1));

        // Update score
        this.score += points;
        this.scoreText.setText(`Score: ${this.score}`);

        // Play the appropriate effect (with null checks)
        this.playGestureEffect(gesture);

        // Show feedback message
        this.showFeedbackMessage(gesture);

        // Update combo
        this.updateCombo();
    }

    createFloatingScore(points, x, y) {
        // Determine color based on points
        let color = '#ffffff';
        if (points >= 50) color = '#ffff00';
        if (points >= 100) color = '#ff9900';

        // Create text
        const pointsText = this.add.text(x, y, `+${points}`, {
            fontFamily: 'Arial',
            fontSize: 24,
            color: color,
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Animate it floating up and fading out
        this.tweens.add({
            targets: pointsText,
            y: y - 100,
            alpha: 0,
            scale: 1.5,
            duration: 1000,
            ease: 'Power1',
            onComplete: () => {
                pointsText.destroy();
            }
        });
    }

    // Enhance the playGestureEffect method
    playGestureEffect(gesture) {
        // Guard clauses
        if (!this.targetSprite || !gesture) {
            return;
        }

        // Safely use currentDirection with defaults if undefined
        const safeDirection = this.currentDirection || { x: 0, y: 0 };

        // Determine effect position based on hand direction
        const targetX = this.targetSprite.x + (safeDirection.x * 30);
        const targetY = this.targetSprite.y + (safeDirection.y * 30);

        try {
            // Create effect (using a simple shape for now)
            let effect;

            // Play appropriate animation and sound
            switch(gesture) {
                case 'flat':
                    // Create a simple effect instead of using sprites
                    effect = this.add.circle(targetX, targetY, 20, 0x00ff00, 0.7);
                    if (this.sound && this.sound.add) {
                        this.sound.play('slap-sound', { volume: 0.5 });
                    }
                    break;
                case 'fist':
                    effect = this.add.circle(targetX, targetY, 25, 0xff0000, 0.7);
                    if (this.sound && this.sound.add) {
                        this.sound.play('punch-sound', { volume: 0.5 });
                    }
                    break;
                case 'guns':
                    effect = this.add.circle(targetX, targetY, 15, 0xffff00, 0.7);
                    if (this.sound && this.sound.add) {
                        this.sound.play('shoot-sound', { volume: 0.5 });
                    }
                    break;
            }

            // Make the target react (safely)
            if (this.targetSprite && this.targetSprite.setTint) {
                this.targetSprite.setTint(0xff0000); // Flash red

                // Reset tint after a delay
                this.time.delayedCall(300, () => {
                    if (this.targetSprite && this.targetSprite.clearTint) {
                        this.targetSprite.clearTint();
                    }
                });
            }

            // Animate and remove the effect
            if (effect) {
                this.tweens.add({
                    targets: effect,
                    alpha: 0,
                    scale: 2,
                    duration: 300,
                    onComplete: () => {
                        effect.destroy();
                    }
                });
            }
        } catch (error) {
            console.error('Error in playGestureEffect:', error);
        }
    }

    showFeedbackMessage(gesture) {
        // Get a random feedback message for the gesture
        const messages = this.feedbackMessages[gesture];
        const message = messages[Math.floor(Math.random() * messages.length)];

        // Display the message
        this.feedbackText.setText(message);
        this.feedbackText.setVisible(true);

        // Hide the message after a delay
        this.time.delayedCall(2000, () => {
            this.feedbackText.setVisible(false);
        });
    }

    updateCombo() {
        // Increment combo
        this.combo++;

        // Update max combo if needed
        if (this.combo > this.maxCombo) {
            this.maxCombo = this.combo;
        }

        // Show combo text
        this.comboText.setText(`Combo: ${this.combo}x`);
        this.comboText.setVisible(true);

        // Play combo sound if combo is significant
        if (this.combo >= 5 && this.combo % 5 === 0) {
            this.sound.play('combo');
        }

        // Reset combo after a delay if no new gestures
        if (this.comboResetTimer) {
            this.comboResetTimer.remove();
        }

        this.comboResetTimer = this.time.delayedCall(3000, () => {
            this.combo = 0;
            this.comboText.setVisible(false);
        });
    }

    // Safe swing guide update
    updateSwingGuide() {
        try {
            // Clear previous guide
            if (this.swingGuideGraphics) {
                this.swingGuideGraphics.clear();
            } else {
                return; // If no graphics object, exit early
            }

            // Get safe values
            const intensity = Math.min((this.currentVelocity || 10) / 50, 1);
            const color = 0x00ff00; // Simple green color

            // Draw a simple guide
            this.swingGuideGraphics.lineStyle(3, color, 0.7);

            // Draw circle guide
            if (this.targetSprite) {
                this.swingGuideGraphics.strokeCircle(this.targetSprite.x, this.targetSprite.y, 60);
            }

            // Draw direction line if we have velocity data
            if (this.currentVelocity && this.currentVelocity > 20 && this.currentDirection && this.targetSprite) {
                const startX = this.targetSprite.x - (this.currentDirection.x || 0) * 80;
                const startY = this.targetSprite.y - (this.currentDirection.y || 0) * 80;
                const endX = this.targetSprite.x + (this.currentDirection.x || 0) * 80;
                const endY = this.targetSprite.y + (this.currentDirection.y || 0) * 80;

                this.swingGuideGraphics.lineStyle(5, color, 0.7);
                this.swingGuideGraphics.beginPath();
                this.swingGuideGraphics.moveTo(startX, startY);
                this.swingGuideGraphics.lineTo(endX, endY);
                this.swingGuideGraphics.strokePath();
            }
        } catch (error) {
            console.error('Error in updateSwingGuide:', error);
        }
    }

    // Update method with error handling
    update() {
        try {
            // Handle any frame-by-frame updates
            if (this.targetSprite) {
                // Simple movement for the target
                const time = this.time.now / 1000;
                const offsetX = Math.sin(time) * 20;
                const offsetY = Math.cos(time * 0.7) * 15;

                this.targetSprite.x = 600 + offsetX;
                this.targetSprite.y = 300 + offsetY;
            }

            // Update the swing guide
            this.updateSwingGuide();
        } catch (error) {
            console.error('Error in update:', error);
        }
    }

    updateTargetCharacter() {
        // Make the target character move slightly for added dynamism
        // This could be expanded with more complex movement patterns
        const time = this.time.now / 1000;
        const offsetX = Math.sin(time) * 20;
        const offsetY = Math.cos(time * 0.7) * 15;

        this.targetSprite.x = 600 + offsetX;
        this.targetSprite.y = 300 + offsetY;
    }

    // Level management methods
    advanceLevel() {
        // Increase level
        this.level++;

        // Update level text with animation
        this.levelText.setText(`Level ${this.level}`);

        // Add level up effect
        const levelUpText = this.add.text(400, 300, 'Level Up!', {
            fontFamily: 'Arial',
            fontSize: 48,
            color: '#ffff00',
            stroke: '#000000',
            strokeThickness: 6
        }).setOrigin(0.5).setAlpha(0);

        // Animate level up text
        this.tweens.add({
            targets: levelUpText,
            alpha: 1,
            y: 250,
            scale: 1.2,
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                this.tweens.add({
                    targets: levelUpText,
                    alpha: 0,
                    duration: 500,
                    delay: 500,
                    onComplete: () => {
                        levelUpText.destroy();
                    }
                });
            }
        });

        // Increase difficulty based on level
        this.updateDifficulty();

        // Add bonus time
        this.addBonusTime(10);
    }

    updateDifficulty() {
        // Increase target movement speed
        this.targetMoveSpeed = 1 + (this.level * 0.2);

        // Add more target types as levels progress
        if (this.level >= 2 && !this.targetTypes.includes('fast')) {
            this.targetTypes.push('fast');
        }

        if (this.level >= 3 && !this.targetTypes.includes('multi-hit')) {
            this.targetTypes.push('multi-hit');
        }

        if (this.level >= 5 && !this.targetTypes.includes('bonus')) {
            this.targetTypes.push('bonus');
        }
    }

    addBonusTime(seconds) {
        // Add time
        this.timeRemaining += seconds;
        if (this.timeRemaining > this.gameTime) {
            this.timeRemaining = this.gameTime;
        }

        // Update display
        this.updateTimerDisplay();

        // Show bonus time effect
        const bonusTimeText = this.add.text(this.timerText.x, this.timerText.y, `+${seconds}`, {
            fontFamily: 'Arial',
            fontSize: 24,
            color: '#00ff00'
        }).setOrigin(1, 0);

        // Animate bonus time text
        this.tweens.add({
            targets: bonusTimeText,
            y: this.timerText.y - 30,
            alpha: 0,
            duration: 1000,
            onComplete: () => {
                bonusTimeText.destroy();
            }
        });
    }

    cleanupResources() {
        // Stop timer
        if (this.timerEvent) {
            this.timerEvent.remove();
        }

        // Existing cleanup code...
    }

    // Cleanup method
    cleanup() {
        try {
            // Stop gesture tracking
            if (this.gestureRecognizer) {
                this.gestureRecognizer.stopTracking();
            }

            // Stop camera
            if (this.cameraManager) {
                this.cameraManager.stopCamera();
            }

            // Remove video element
            if (this.videoElement && document.body.contains(this.videoElement)) {
                document.body.removeChild(this.videoElement);
            }
        } catch (error) {
            console.error('Error in cleanup:', error);
        }
    }

    shutdown() {
        // Clean up resources when scene is closed
        this.cleanup();
        this.cleanupResources();

        if (this.gestureRecognizer) {
            this.gestureRecognizer.stopTracking();
        }

        if (this.cameraManager) {
            this.cameraManager.stopCamera();
        }

        // Remove video element
        if (this.videoElement && document.body.contains(this.videoElement)) {
            document.body.removeChild(this.videoElement);
        }
    }
}

export default GameplayScene;